/**
 * AuthProvider para Refine.dev con soporte completo de refresh tokens
 * Implementa autenticación con access/refresh tokens y multi-dispositivo
 */

import { AuthProvider } from "@pankod/refine-core";
import { authService } from "services/auth.service";
import { StorageUtil } from "utils/storage.util";
import { DeviceUtil } from "utils/device.util";

export const authProvider = (setPermissions: (permissions: string) => void): AuthProvider => {
  // Inicializar deviceId al cargar la aplicación
  DeviceUtil.getOrCreateDeviceId();

  return {
    /**
     * Login con username y password
     * Guarda access token, refresh token y deviceId
     */
    login: async ({ username, password }) => {
      try {
        const response = await authService.login({ username, password });

        // Actualizar permisos en el estado de la aplicación
        if (response.permissions) {
          setPermissions(response.permissions);
        }

        console.log('[Auth] Login successful');
        return Promise.resolve(response.defaultPage || '/');
      } catch (error: any) {
        console.error('[Auth] Login failed:', error);

        if (error.response?.status === 401) {
          return Promise.reject({
            name: 'Credenciales no válidas',
            message: 'El usuario o contraseña no son válidos',
          });
        }

        return Promise.reject({
          name: 'Error de conexión',
          message: error.message || 'No se pudo conectar con el servidor',
        });
      }
    },

    /**
     * Logout
     * Invalida el refresh token en el backend y limpia el localStorage
     */
    logout: async () => {
      try {
        await authService.logout();
        console.log('[Auth] Logout successful');
        return Promise.resolve();
      } catch (error) {
        console.error('[Auth] Logout error:', error);
        // Aunque falle el logout en el backend, limpiar tokens locales
        StorageUtil.clearTokens();
        return Promise.resolve();
      }
    },

    /**
     * Verificar errores de autenticación
     * Llamado cuando hay errores en las peticiones
     */
    checkError: (error: any) => {
      const status = error?.response?.status;

      if (status === 401 || status === 403) {
        console.log('[Auth] Authentication error detected');
        return Promise.reject();
      }

      return Promise.resolve();
    },

    /**
     * Verificar autenticación
     * Llamado en cada cambio de ruta para verificar si el usuario está autenticado
     */
    checkAuth: () => {
      const hasValidSession = StorageUtil.hasValidSession();

      if (hasValidSession) {
        return Promise.resolve();
      }

      console.log('[Auth] No valid session found');
      return Promise.reject();
    },

    /**
     * Obtener permisos del usuario actual
     */
    getPermissions: async () => {
      try {
        // Primero intentar obtener permisos del localStorage
        const cachedPermissions = StorageUtil.getPermissions();
        
        if (cachedPermissions) {
          setPermissions(cachedPermissions);
          return Promise.resolve(cachedPermissions.split(','));
        }

        // Si no hay permisos en cache, obtenerlos del servidor
        const permissions = await authService.getPermissions();
        const permissionsString = permissions.join(',');
        setPermissions(permissionsString);

        return Promise.resolve(permissions);
      } catch (error) {
        console.error('[Auth] Failed to get permissions:', error);
        return Promise.reject();
      }
    },

    /**
     * Obtener identidad del usuario actual
     * Puede ser expandido para obtener más información del usuario
     */
    getUserIdentity: async () => {
      const token = StorageUtil.getAccessToken();
      
      if (!token) {
        return Promise.reject();
      }

      // TODO: Implementar endpoint para obtener información del usuario
      // Por ahora retornar datos básicos
      return Promise.resolve({
        id: 1,
        name: 'Usuario',
        avatar: undefined,
      });
    },
  };
};
